import { useState, useEffect, useRef } from "react";
import { suppliersApi, type Supplier, ApiError } from "@/lib/api";

interface SupplierPickerProps {
  value: number | null;
  valueName?: string;
  onChange: (supplier: Supplier | null) => void;
  error?: string;
}

export default function SupplierPicker({ value, valueName, onChange, error }: SupplierPickerProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedName, setSelectedName] = useState(valueName ?? "");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!showDropdown) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    suppliersApi.list(1, debouncedQuery ? { search: debouncedQuery } : {})
      .then((res) => {
        if (requestIdRef.current === requestId) setResults(res.data.data);
      })
      .catch(() => {
        if (requestIdRef.current === requestId) setResults([]);
      })
      .finally(() => {
        if (requestIdRef.current === requestId) setLoading(false);
      });
  }, [debouncedQuery, showDropdown]);

  const handleSelect = (supplier: Supplier) => {
    setSelectedName(supplier.name);
    setQuery("");
    setCreateError(null);
    setShowDropdown(false);
    onChange(supplier);
  };

  const handleCreate = async () => {
    const name = debouncedQuery.trim();
    if (!name) return;
    setCreating(true);
    setCreateError(null);
    try {
      const res = await suppliersApi.create({ name });
      handleSelect(res.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setCreateError(`"${name}" may already exist — try searching again.`);
        setDebouncedQuery(name);
      } else {
        setCreateError("Failed to create supplier.");
      }
    } finally {
      setCreating(false);
    }
  };

  const displayValue = value ? selectedName : query;

  return (
    <div className="relative">
      <input
        type="text"
        placeholder="Search supplier..."
        value={displayValue}
        onChange={(e) => {
          onChange(null);
          setSelectedName("");
          setQuery(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        className={`w-full h-9 px-3 text-sm border rounded-md bg-white text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:ring-1 transition-colors ${
          error ? "border-accent-400 focus:border-accent-400 focus:ring-accent-400" : "border-background-300 focus:border-primary-400 focus:ring-primary-400"
        }`}
      />
      {error && <p className="text-accent-600 text-xs mt-1">{error}</p>}
      {showDropdown && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-md border border-background-200 shadow-lg z-20 max-h-48 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-foreground-400">Searching…</div>
          ) : results.length > 0 ? (
            results.map((s) => (
              <button
                key={s.id}
                onMouseDown={() => handleSelect(s)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-background-100 transition-colors cursor-pointer ${
                  s.id === value ? "bg-primary-50 text-primary-600 font-medium" : "text-foreground-700"
                }`}
              >
                {s.name}
              </button>
            ))
          ) : debouncedQuery.trim() !== "" ? (
            <button
              onMouseDown={handleCreate}
              disabled={creating}
              className="w-full text-left px-3 py-2 text-sm text-primary-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-60"
            >
              {creating ? "Creating…" : `+ Create "${debouncedQuery.trim()}" as new supplier`}
            </button>
          ) : (
            <div className="px-3 py-2 text-sm text-foreground-400">No suppliers found</div>
          )}
          {createError && <div className="px-3 py-2 text-xs text-accent-600 border-t border-background-200">{createError}</div>}
        </div>
      )}
    </div>
  );
}
